import { styled } from "goober";
import { Fragment } from "react";
import {
  type ConnectorWithOAuth,
  isItemVisible,
  targetIsSet,
} from "../../../shared/connectors/getTargetValue";
import { CA_CERTIFICATE_FIELDS } from "../../../shared/connectors/caCertificate";
import { normalizeConnectorUrlPattern } from "../../../shared/connectors/normalizeUrlPattern";
import { isResourceListField } from "../../../shared/connectors/listRows";
import type {
  ConnectorDefinition,
  FileField,
  ListField,
  ListFieldOption,
  StepItem,
  UrlPatternField as UrlPatternFieldType,
} from "../../../shared/connectors/types";
import { toSentence } from "../../lib/toSentence";
import { Box } from "../Box";
import { Text } from "../Text";
import { CopyableFieldItem } from "./CopyableFieldItem";
import { FieldItem } from "./FieldItem";
import { AdvancedSection } from "./AdvancedSection";
import { InstructionsStep, StepHeading } from "./InstructionsStep";
import { FileFieldItem } from "./FileFieldItem";
import { ListFieldItem } from "./ListFieldItem";
import { ResourceListField } from "./ResourceListField";
import { MarkdownItem } from "./MarkdownItem";
import { NoteField } from "./NoteField";
import { GithubAppManifestFieldItem } from "./GithubAppManifestFieldItem";
import { ConnectorAuthorizationFieldItem } from "./ConnectorAuthorizationFieldItem";
import { OAuthFieldItem } from "./OAuthFieldItem";
import { TestUrlField } from "./TestUrlField";
import { TrustedCertificateField } from "./TrustedCertificateField";
import type { ConnectorAuthorize } from "./useConnectorAuthorize";
import { UrlPatternField } from "./UrlPatternField";
import { ConnectorRoutesItem } from "./ConnectorRoutesItem";
import { WorkflowPickerItem } from "./WorkflowPickerItem";

const Container = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export type App = ConnectorWithOAuth & {
  visitorGrants?: readonly unknown[];
};

export const isMcpToolsField = (item: StepItem): item is ListField =>
  item.kind === "list" && item.fetch?.protocol === "mcp";

export const isModelAccessField = (
  item: StepItem
): item is ListField & { options: readonly ListFieldOption[] } =>
  item.kind === "list" && !!item.options;

type Props = {
  definition: ConnectorDefinition;
  app: App;
  showInstructions?: boolean;
  disabled?: boolean;
  onEdit?: () => void;
  fieldSaveErrors: ReadonlySet<string>;
  onFieldSaveStatus: (field: string, saved: boolean) => void;
  locallySet: ReadonlySet<string>;
  onFieldSet: (key: string) => void;
  auth: ConnectorAuthorize;
};

const Steps = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const isAdvanced = (item: StepItem): item is FileField | UrlPatternFieldType =>
  (item.kind === "file" || item.kind === "url-pattern") && !!item.advanced;

const onlyAuthorizes = (items: StepItem[]) =>
  items.some((item) => item.kind === "oauth") &&
  items.every((item) => item.kind === "oauth" || item.kind === "markdown");

const advancedLabel = (item: FileField | UrlPatternFieldType) =>
  item.kind === "url-pattern" ? "Allowed URL patterns" : item.label;

const itemKey = (item: StepItem) => {
  switch (item.kind) {
    case "markdown":
      return typeof item.content === "string"
        ? item.content
        : item.content.toString();
    case "copyable":
      return item.label;
    case "oauth":
    case "authorization":
    case "githubAppManifest":
      return item.kind;
    case "url-pattern":
      return item.target.field;
    case "list":
      return `${item.prefix}:${item.label}`;
    case "test-url":
      return item.urlTarget.field;
    case "workflow-picker":
    case "exposed-routes":
      return `${item.kind}:${item.label}`;
    case "file":
      return `${item.target.kind}:${item.target.field}`;
    default:
      return `${item.target.kind}:${item.target.field}:${item.label}`;
  }
};

export function Form({
  definition,
  app,
  showInstructions = true,
  disabled,
  onEdit,
  fieldSaveErrors,
  onFieldSaveStatus,
  locallySet,
  onFieldSet,
  auth,
}: Props) {
  const hidesOwnerAuthorization =
    app.authorizationMode === "visitor" &&
    definition.oauth?.clientCredentialsFrom !== "dcr";
  const isVisible = (item: StepItem) =>
    !isMcpToolsField(item) &&
    !isModelAccessField(item) &&
    !(hidesOwnerAuthorization && item.kind === "oauth") &&
    isItemVisible(item, app, definition);
  const steps = definition.steps
    .filter((step) => !(hidesOwnerAuthorization && onlyAuthorizes(step.items)))
    .map((step) => ({
      title: step.title,
      items: step.items.filter((item) => isVisible(item) && !isAdvanced(item)),
    }))
    .filter((step) => step.items.length > 0)
    .map((step, stepIndex, visibleSteps) => ({
      ...step,
      title:
        visibleSteps.length > 1
          ? `Step ${stepIndex + 1}: ${step.title}`
          : step.title,
    }));
  const advancedItems = definition.steps
    .flatMap((step) => step.items.filter(isAdvanced))
    .filter(isVisible);
  const isCustomized = (item: StepItem) =>
    item.kind === "url-pattern"
      ? normalizeConnectorUrlPattern(
          app.urlPattern,
          app.identifier,
          app.configuration
        ) !==
        normalizeConnectorUrlPattern("", app.identifier, app.configuration)
      : "target" in item && targetIsSet(item.target, app);
  const renderItem = (item: StepItem) => (
    <FormItem
      key={itemKey(item)}
      item={item}
      definition={definition}
      app={app}
      showInstructions={showInstructions}
      disabled={disabled}
      onEdit={onEdit}
      fieldSaveErrors={fieldSaveErrors}
      onFieldSaveStatus={onFieldSaveStatus}
      locallySet={locallySet}
      onFieldSet={onFieldSet}
      auth={auth}
    />
  );

  return (
    <Container autoComplete="off">
      {showInstructions ? (
        <Steps>
          {steps.map((step) => (
            <InstructionsStep key={step.title}>
              <StepHeading
                style={{
                  marginBottom: step.items[0]?.kind === "markdown" ? -6 : 0,
                }}
              >
                <Text variant="text-regular-md">{step.title}</Text>
              </StepHeading>
              {step.items.map(renderItem)}
            </InstructionsStep>
          ))}
        </Steps>
      ) : (
        <InstructionsStep style={{ gap: 16 }}>
          {steps.map((step) => (
            <Fragment key={step.title}>{step.items.map(renderItem)}</Fragment>
          ))}
        </InstructionsStep>
      )}
      {advancedItems.length > 0 && (
        <AdvancedSection
          defaultOpen={advancedItems.some(isCustomized)}
          summary={toSentence(advancedItems.map(advancedLabel))}
        >
          {advancedItems.map(renderItem)}
        </AdvancedSection>
      )}
    </Container>
  );
}

type FormItemProps = Omit<Props, "showInstructions"> & {
  item: StepItem;
  showInstructions: boolean;
};

function FormItem({
  item,
  definition,
  app,
  showInstructions,
  disabled,
  onEdit,
  fieldSaveErrors,
  onFieldSaveStatus,
  locallySet,
  onFieldSet,
  auth,
}: FormItemProps) {
  switch (item.kind) {
    case "markdown":
      return showInstructions ? (
        <MarkdownItem
          item={item}
          configuration={app.configuration}
          connectedAppId={app.id}
        />
      ) : null;
    case "copyable":
      return <CopyableFieldItem item={item} app={app} />;
    case "oauth":
      return (
        <Box top={showInstructions ? 0 : 10}>
          <OAuthFieldItem
            app={app}
            definition={definition}
            disabled={disabled}
            auth={auth}
          />
        </Box>
      );
    case "authorization":
      return (
        <ConnectorAuthorizationFieldItem
          item={item}
          definition={definition}
          app={app}
          disabled={disabled}
        />
      );
    case "githubAppManifest":
      return (
        <GithubAppManifestFieldItem
          app={app}
          definition={definition}
          disabled={disabled}
        />
      );
    case "file":
      return (
        <>
          <FileFieldItem
            item={item}
            app={app}
            disabled={disabled}
            onEdit={onEdit}
            saveError={fieldSaveErrors.has(
              `file:${item.target.kind}:${item.target.field}`
            )}
            onSaveStatus={(saved) =>
              onFieldSaveStatus(
                `file:${item.target.kind}:${item.target.field}`,
                saved
              )
            }
          />
          {item.target.kind === "configuration" &&
            CA_CERTIFICATE_FIELDS.includes(item.target.field) && (
              <TrustedCertificateField
                app={app}
                definition={definition}
                field={item.target.field}
                disabled={disabled}
                onEdit={onEdit}
                saveError={fieldSaveErrors.has(
                  `certificate:${item.target.field}`
                )}
                onSaveStatus={(field, saved) =>
                  onFieldSaveStatus(`certificate:${field}`, saved)
                }
              />
            )}
        </>
      );
    case "url-pattern":
      return (
        <UrlPatternField
          item={item}
          app={app}
          disabled={disabled}
          onEdit={onEdit}
          saveError={fieldSaveErrors.has("urlPattern")}
          onSaveStatus={(saved) => onFieldSaveStatus("urlPattern", saved)}
        />
      );
    case "list":
      if (isResourceListField(item))
        return (
          <ResourceListField
            item={item}
            app={app}
            disabled={disabled}
            onEdit={onEdit}
          />
        );
      return (
        <ListFieldItem
          item={item}
          app={app}
          disabled={disabled}
          onEdit={onEdit}
          saveErrors={fieldSaveErrors}
          onFieldSaveStatus={onFieldSaveStatus}
        />
      );
    case "test-url":
      return (
        <TestUrlField
          item={item}
          app={app}
          disabled={disabled}
          onEdit={onEdit}
        />
      );
    case "workflow-picker":
      return <WorkflowPickerItem item={item} app={app} disabled={disabled} />;
    case "exposed-routes":
      return <ConnectorRoutesItem item={item} app={app} />;
    default:
      return (
        <>
          <FieldItem
            item={item}
            app={app}
            locallySet={locallySet}
            onFieldSet={onFieldSet}
            disabled={disabled}
            onEdit={onEdit}
          />
          {item.target.kind === "connectedApp" &&
            item.target.field === "name" && (
              <NoteField app={app} disabled={disabled} />
            )}
        </>
      );
  }
}
